import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in';

const OwnerDashboard = ({ onNavigate, userInfo }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (userInfo?.userId) {
      fetchDashboardData();
    }
  }, [userInfo]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/owner/dashboard/${userInfo.userId}`);
      const data = await response.json();
      if (data.success) {
        setDashboardData(data);
      } else {
        setError(data.message || 'Failed to load dashboard data');
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleNavigation = (page) => {
    if (isMobile) {
      setSidebarOpen(false);
    }
    if (onNavigate) {
      onNavigate(page);
    }
  };

  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    if (onNavigate) {
      onNavigate('hostel-login');
    }
  };

  const MenuItem = ({ label, icon, onClick }) => (
    <div className="menu-item" onClick={onClick}>
      <span className="menu-icon">{icon}</span>
      <span className="menu-label">{label}</span>
    </div>
  );

  const StatCard = ({ emoji, label, value, subtext, color }) => (
    <div className="stat-card">
      <div className="stat-emoji">{emoji}</div>
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color }}>{value}</div>
      <div className="stat-subtext">{subtext}</div>
    </div>
  );

  const QuickActionButton = ({ icon, label, onClick, color }) => (
    <button className="quick-action-btn" onClick={onClick} style={{ borderColor: color }}>
      <div className="quick-action-icon" style={{ color }}>{icon}</div>
      <div className="quick-action-label">{label}</div>
    </button>
  );

  const getMetrics = () => {
    if (!dashboardData?.stats) {
      return [
        { emoji: '🏢', label: 'Total Hostels', value: '0', subtext: 'All hostels' },
        { emoji: '👥', label: 'Total Students', value: '0', subtext: '0% occupied' },
        { emoji: '🚪', label: 'Available Rooms', value: '0', subtext: '0 rooms' },
        { emoji: '📊', label: 'Occupancy Rate', value: '0%', subtext: '0 rooms' },
      ];
    }

    const stats = dashboardData.stats;
    const totalHostels = stats.total_hostels || 0;
    const totalRooms = stats.total_rooms || 0;
    const availableRooms = stats.available_rooms || 0;
    const occupiedRooms = totalRooms - availableRooms;
    const totalStudents = stats.total_students || 0;

    return [
      {
        emoji: '🏢',
        label: 'Total Hostels',
        value: totalHostels.toString(),
        subtext: 'All properties',
        color: '#0d9488'
      },
      {
        emoji: '👥',
        label: 'Total Students',
        value: totalStudents.toString(),
        subtext: `${totalRooms > 0 ? Math.round((totalStudents / totalRooms) * 100) : 0}% occupancy`,
        color: '#14b8a6'
      },
      {
        emoji: '🚪',
        label: 'Available Rooms',
        value: availableRooms.toString(),
        subtext: `${totalRooms} total rooms`,
        color: '#06b6d4'
      },
      {
        emoji: '📊',
        label: 'Occupancy Rate',
        value: `${totalRooms > 0 ? Math.round((occupiedRooms / totalRooms) * 100) : 0}%`,
        subtext: `${occupiedRooms}/${totalRooms} rooms`,
        color: '#8b5cf6'
      }
    ];
  };

  const OccupancyChart = () => {
    const stats = dashboardData?.stats || {};
    const totalRooms = stats.total_rooms || 100;
    const availableRooms = stats.available_rooms || 30;
    const occupiedRooms = totalRooms - availableRooms;
    const occupancyPercentage = totalRooms > 0 ? Math.round((occupiedRooms / totalRooms) * 100) : 0;

    const chartData = [
      { label: 'Occupied', value: occupiedRooms, percentage: occupancyPercentage, color: '#0d9488' },
      { label: 'Available', value: availableRooms, percentage: 100 - occupancyPercentage, color: '#5eead4' }
    ];

    return (
      <div className="occupancy-chart-container">
        <div className="chart-stats">
          {chartData.map((item, index) => (
            <div key={index} className="chart-stat-item">
              <div className="stat-header">
                <div className="stat-color" style={{ backgroundColor: item.color }}></div>
                <span className="stat-label">{item.label}</span>
              </div>
              <div className="stat-value">{item.value} rooms</div>
              <div className="stat-percentage">{item.percentage}%</div>
            </div>
          ))}
        </div>
        <div className="chart-visual">
          <svg viewBox="0 0 200 200" className="donut-chart">
            <circle cx="100" cy="100" r="80" fill="none" stroke="#5eead4" strokeWidth="30" />
            <circle 
              cx="100" 
              cy="100" 
              r="80" 
              fill="none" 
              stroke="#0d9488" 
              strokeWidth="30"
              strokeDasharray={`${occupancyPercentage * 5.03} ${(100 - occupancyPercentage) * 5.03}`}
              transform="rotate(-90 100 100)"
            />
            <text x="100" y="95" textAnchor="middle" fontSize="32" fontWeight="bold" fill="#0d9488">
              {occupancyPercentage}%
            </text>
            <text x="100" y="115" textAnchor="middle" fontSize="14" fill="#64748b">
              Occupied
            </text>
          </svg>
        </div>
      </div>
    );
  };

  const metrics = getMetrics();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .dashboard-container {
          display: flex;
          min-height: 100vh;
          background: linear-gradient(135deg, #f0fdfa 0%, #e0f2fe 100%);
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          color: #1e293b;
        }

        /* Sidebar Styles */
        .sidebar {
          width: 280px;
          background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
          border-right: 1px solid #e2e8f0;
          box-shadow: 2px 0 10px rgba(0, 0, 0, 0.05);
          position: fixed;
          left: 0;
          top: 0;
          height: 100vh;
          display: flex;
          flex-direction: column;
          transition: transform 0.3s ease;
          z-index: 1000;
          overflow-y: auto;
        }

        .sidebar.closed {
          transform: translateX(-280px);
        }

        .sidebar.opened {
          transform: translateX(0);
        }

        .sidebar-overlay {
          display: none;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.3);
          z-index: 999;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        @media (max-width: 768px) {
          .sidebar-overlay.active {
            display: block;
            opacity: 1;
          }
        }

        .user-info-sidebar {
          padding: 30px 20px;
          display: flex;
          align-items: center;
          gap: 15px;
          border-bottom: 1px solid #e2e8f0;
        }

        .user-avatar {
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, #0d9488 0%, #14b8a6 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 700;
          font-size: 20px;
          flex-shrink: 0;
        }

        .user-name {
          font-weight: 600;
          color: #1e293b;
        }

        .user-role {
          font-size: 12px;
          color: #64748b;
        }

        .sidebar-nav {
          flex: 1;
          padding: 30px 0;
        }

        .nav-label {
          padding: 0 20px;
          font-size: 12px;
          font-weight: 700;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 15px;
        }

        .menu-item {
          padding: 15px 20px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 15px;
          color: #64748b;
          transition: all 0.2s ease;
          border-left: 3px solid transparent;
        }

        .menu-item:hover {
          background: #f1f5f9;
          color: #0d9488;
          border-left-color: #0d9488;
        }

        .menu-icon {
          font-size: 18px;
        }

        .menu-label {
          font-weight: 500;
        }

        .logout-section {
          padding: 20px;
          border-top: 1px solid #e2e8f0;
        }

        .logout-btn {
          width: 100%;
          padding: 12px;
          background: #fee2e2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          color: #dc2626;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .logout-btn:hover {
          background: #fecaca;
        }

        /* Main Content */
        .main-content {
          flex: 1;
          margin-left: 280px;
          display: flex;
          flex-direction: column;
          transition: margin-left 0.3s ease;
        }

        .main-content.full {
          margin-left: 0;
        }

        .header {
          background: white;
          padding: 20px 30px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .menu-toggle {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #0d9488;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .menu-toggle:hover {
          background: #f1f5f9;
        }

        .header-title {
          font-size: 24px;
          font-weight: 700;
          color: #0d9488;
        }

        .content-area {
          flex: 1;
          padding: 30px;
          overflow-y: auto;
        }

        .section-title {
          font-size: 18px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 20px;
          margin-top: 30px;
        }

        .section-title:first-child {
          margin-top: 0;
        }

        /* Quick Actions Section */
        .quick-actions-section {
          margin-bottom: 30px;
        }

        .quick-actions-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 15px;
        }

        .quick-action-btn {
          background: white;
          border: 2px solid;
          border-radius: 12px;
          padding: 20px;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          transition: all 0.3s ease;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        }

        .quick-action-btn:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
        }

        .quick-action-icon {
          font-size: 32px;
        }

        .quick-action-label {
          font-weight: 600;
          font-size: 14px;
          text-align: center;
          color: #1e293b;
        }

        /* Metrics Grid */
        .metrics-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }

        .stat-card {
          background: white;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
          transition: all 0.3s ease;
          border: 1px solid #e2e8f0;
          text-align: center;
        }

        .stat-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
        }

        .stat-emoji {
          font-size: 32px;
          margin-bottom: 10px;
        }

        .stat-label {
          font-size: 12px;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          margin-bottom: 8px;
        }

        .stat-value {
          font-size: 32px;
          font-weight: 700;
          margin-bottom: 8px;
        }

        .stat-subtext {
          font-size: 12px;
          color: #64748b;
        }

        /* Occupancy Chart Section */
        .chart-section {
          background: white;
          border-radius: 12px;
          padding: 25px;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
        }

        .chart-title {
          font-size: 16px;
          font-weight: 700;
          color: #0d9488;
          margin-bottom: 20px;
        }

        .occupancy-chart-container {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 40px;
          align-items: center;
        }

        .chart-stats {
          display: flex;
          flex-direction: column;
          gap: 30px;
        }

        .chart-stat-item {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .stat-header {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .stat-color {
          width: 12px;
          height: 12px;
          border-radius: 2px;
        }

        .stat-value {
          font-size: 28px;
          font-weight: 700;
          color: #0d9488;
          margin-bottom: 5px;
        }

        .stat-percentage {
          font-size: 14px;
          color: #64748b;
        }

        .chart-visual {
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .donut-chart {
          width: 100%;
          max-width: 250px;
          height: auto;
          filter: drop-shadow(0 4px 20px rgba(13, 148, 136, 0.2));
        }

        .error-message {
          background: #fee2e2;
          border: 1px solid #fecaca;
          border-radius: 12px;
          padding: 20px;
          color: #dc2626;
          text-align: center;
          margin: 20px;
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          gap: 20px;
        }

        .spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #e2e8f0;
          border-top-color: #0d9488;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Responsive Design */
        @media (max-width: 1200px) {
          .occupancy-chart-container {
            grid-template-columns: 1fr;
            gap: 30px;
          }

          .chart-visual {
            order: -1;
          }
        }

        @media (max-width: 768px) {
          .sidebar {
            transform: translateX(-280px);
          }

          .sidebar.opened {
            transform: translateX(0);
            box-shadow: 4px 0 20px rgba(0, 0, 0, 0.15);
          }

          .main-content {
            margin-left: 0 !important;
          }

          .header {
            padding: 12px 16px;
          }

          .header-title {
            font-size: 18px;
          }

          .content-area {
            padding: 20px 15px;
          }

          .metrics-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 15px;
          }

          .quick-actions-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
          }

          .stat-value {
            font-size: 24px;
          }

          .chart-section {
            padding: 20px 15px;
          }

          .section-title {
            font-size: 16px;
            margin-bottom: 15px;
          }

          .occupancy-chart-container {
            gap: 20px;
          }

          .chart-stats {
            gap: 20px;
          }
        }

        @media (max-width: 480px) {
          .header {
            padding: 10px 12px;
          }

          .header-title {
            font-size: 16px;
          }

          .content-area {
            padding: 15px 10px;
          }

          .metrics-grid {
            grid-template-columns: 1fr;
            gap: 12px;
          }

          .quick-actions-grid {
            grid-template-columns: 1fr;
            gap: 10px;
          }

          .stat-card {
            padding: 15px;
          }

          .stat-value {
            font-size: 20px;
          }

          .stat-emoji {
            font-size: 24px;
          }

          .quick-action-btn {
            padding: 15px;
          }

          .quick-action-icon {
            font-size: 28px;
          }

          .chart-section {
            padding: 15px;
          }

          .section-title {
            font-size: 14px;
            margin-bottom: 12px;
          }

          .occupancy-chart-container {
            gap: 15px;
          }

          .chart-stats {
            gap: 15px;
          }

          .stat-value {
            font-size: 18px;
          }

          .donut-chart {
            max-width: 200px;
          }
        }
      `}</style>

      <div className="dashboard-container">
        {/* Sidebar Overlay for Mobile */}
        {isMobile && (
          <div 
            className={`sidebar-overlay ${sidebarOpen ? 'active' : ''}`}
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <div className={`sidebar ${!sidebarOpen ? 'closed' : ''} ${isMobile && sidebarOpen ? 'opened' : ''}`}>
          <div className="user-info-sidebar">
            <div className="user-avatar">
              {userInfo?.fullName?.charAt(0)?.toUpperCase() || 'O'}
            </div>
            <div>
              <div className="user-name">{userInfo?.fullName || 'Owner'}</div>
              <div className="user-role">Property Owner</div>
            </div>
          </div>

          <div className="sidebar-nav">
            <div className="nav-label">Main Menu</div>
            <MenuItem
              label="Dashboard"
              icon="📊"
              onClick={() => handleNavigation('hostel-owner-dashboard')}
            />
            <MenuItem
              label="Inventory Management"
              icon="📦"
              onClick={() => handleNavigation('hostel-owner-inventory')}
            />
            <MenuItem
              label="See Hostel Data"
              icon="🏢"
              onClick={() => handleNavigation('hostel-owner-hosteldata')}
            />
          </div>

          <div className="logout-section">
            <button className="logout-btn" onClick={handleLogout}>
              🚪 Logout
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className={`main-content ${!sidebarOpen ? 'full' : ''}`}>
          <div className="header">
            <div className="header-left">
              <button
                className="menu-toggle"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                title={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
              >
                {sidebarOpen ? '✕' : '☰'}
              </button>
              <div className="header-title">📊 Owner Dashboard</div>
            </div>
          </div>

          <div className="content-area">
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            {/* Quick Actions Section */}
            <div className="quick-actions-section">
              <div className="section-title">⚡ Quick Actions</div>
              <div className="quick-actions-grid">
                <QuickActionButton
                  icon="📦"
                  label="Manage Inventory"
                  onClick={() => handleNavigation('hostel-owner-inventory')}
                  color="#0d9488"
                />
                <QuickActionButton
                  icon="🏢"
                  label="View Hostels"
                  onClick={() => handleNavigation('hostel-owner-hosteldata')}
                  color="#14b8a6"
                />
                <QuickActionButton
                  icon="👥"
                  label="Student Records"
                  onClick={() => handleNavigation('hostel-owner-dashboard')}
                  color="#06b6d4"
                />
                <QuickActionButton
                  icon="📊"
                  label="Analytics"
                  onClick={() => handleNavigation('hostel-owner-dashboard')}
                  color="#8b5cf6"
                />
              </div>
            </div>

            {/* Metrics Grid */}
            <div className="section-title">📈 Key Metrics</div>
            <div className="metrics-grid">
              {metrics.map((metric, index) => (
                <StatCard 
                  key={index} 
                  emoji={metric.emoji}
                  label={metric.label}
                  value={metric.value}
                  subtext={metric.subtext}
                  color={metric.color}
                />
              ))}
            </div>

            {/* Occupancy Chart */}
            <div className="chart-section">
              <div className="chart-title">🏠 Room Occupancy Distribution</div>
              <OccupancyChart />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OwnerDashboard;